// GPS tracking + trip math + voice for Jixxeo Speedometer.
import { useCallback, useEffect, useRef, useState } from "react";

export const toUnit = (mps, unit) => mps * (unit === "kmh" ? 3.6 : 2.236936);
export const fromUnit = (v, unit) => v / (unit === "kmh" ? 3.6 : 2.236936);
export const distUnit = (m, unit) => unit === "kmh" ? m / 1000 : m / 1609.344;

export function haversine(a, b) {
  const R = 6371000, r = Math.PI / 180;
  const dLat = (b[0] - a[0]) * r, dLon = (b[1] - a[1]) * r;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * r) * Math.cos(b[0] * r) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

const emptyTrip = () => ({ running: false, paused: false, startedAt: null, distance: 0, max: 0, moving: 0, over: 0, path: [], elapsedBase: 0, resumedAt: null });

export function useSpeedo(target) {
  const [fix, setFix] = useState(null);
  const [speed, setSpeed] = useState(0);
  const [error, setError] = useState("");
  const [sim, setSim] = useState(false);
  const [trip, setTrip] = useState(emptyTrip);
  const last = useRef(null);
  const speedRef = useRef(0);
  const tripRef = useRef(trip);
  tripRef.current = trip;
  const targetRef = useRef(target);
  targetRef.current = target;

  const onPos = useCallback((p) => {
    const { latitude, longitude, accuracy, speed: sp } = p.coords;
    const pt = [latitude, longitude], t = p.timestamp || Date.now();
    let v = sp != null && !Number.isNaN(sp) ? sp : null;
    const prev = last.current;
    if (prev && v == null) { const dt = (t - prev.t) / 1000; if (dt > 0) v = haversine(prev.pt, pt) / dt; }
    v = Math.max(0, v || 0);
    if (v < 0.45 || accuracy > 60) v = accuracy > 60 ? speedRef.current : 0;
    setFix({ lat: latitude, lon: longitude, accuracy, t });
    setSpeed(v); speedRef.current = v;
    setError("");
    const tr = tripRef.current;
    if (tr.running && !tr.paused && prev && accuracy <= 60) {
      const d = haversine(prev.pt, pt), dt = (t - prev.t) / 1000;
      const moving = v >= 0.9 && d > 0.5;
      setTrip((x) => ({ ...x, distance: x.distance + (moving ? d : 0), max: Math.max(x.max, v), moving: x.moving + (moving ? dt : 0),
        over: x.over + (targetRef.current && v > targetRef.current ? dt : 0),
        path: moving && (x.path.length === 0 || haversine(x.path[x.path.length - 1], pt) > 25) ? [...x.path, pt].slice(-600) : x.path }));
    }
    last.current = { pt, t };
  }, []);

  useEffect(() => {
    if (sim) {
      let lat = 37.7749, lon = -122.4194, k = 0;
      const id = setInterval(() => {
        k += 1;
        const mps = Math.max(0, 15 + 13 * Math.sin(k / 12) + (Math.random() - 0.5) * 1.5);
        lat += (mps / 111320) * Math.cos(k / 60); lon += (mps / (111320 * Math.cos(lat * Math.PI / 180))) * Math.sin(k / 60);
        onPos({ coords: { latitude: lat, longitude: lon, accuracy: 5, speed: mps }, timestamp: Date.now() });
      }, 1000);
      return () => clearInterval(id);
    }
    if (!("geolocation" in navigator)) { setError("This device has no GPS / location support."); return; }
    const id = navigator.geolocation.watchPosition(onPos, (e) => setError(e.code === 1 ? "Location permission denied — allow location for this site to see your speed." : "Waiting for a GPS signal… go outside or near a window."),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 });
    return () => navigator.geolocation.clearWatch(id);
  }, [sim, onPos]);

  const elapsed = (tr = trip) => tr.elapsedBase + (tr.running && !tr.paused && tr.resumedAt ? (Date.now() - tr.resumedAt) / 1000 : 0);
  const start = () => { last.current = null; setTrip({ ...emptyTrip(), running: true, startedAt: new Date().toISOString(), resumedAt: Date.now() }); };
  const pause = () => setTrip((x) => x.paused ? { ...x, paused: false, resumedAt: Date.now() } : { ...x, paused: true, elapsedBase: elapsed(x), resumedAt: null });
  const stop = () => { const tr = { ...tripRef.current, duration: elapsed(tripRef.current), endedAt: new Date().toISOString() }; setTrip(emptyTrip()); return tr; };

  return { fix, speed, error, sim, setSim, trip, elapsed, start, pause, stop };
}

export function speak(text) {
  if (!("speechSynthesis" in window)) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text); u.rate = 1.02;
  window.speechSynthesis.speak(u);
}

const WORDS = { zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, fifteen: 15,
  twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90, hundred: 100 };
export function parseNumber(s) {
  const d = s.match(/\d+/); if (d) return Number(d[0]);
  let n = 0, found = false;
  s.split(/[\s-]+/).forEach((w) => { if (WORDS[w] != null) { found = true; n = WORDS[w] === 100 ? (n || 1) * 100 : n + WORDS[w]; } });
  return found ? n : null;
}

export function parseCommand(raw) {
  const s = raw.toLowerCase().trim();
  if (/(clear|remove|cancel|no) (the )?(target|limit)/.test(s)) return { cmd: "clear_target" };
  if (/(target|limit|alert me (at|above)|warn me)/.test(s)) { const n = parseNumber(s); if (n) return { cmd: "target", value: n }; }
  if (/(start|begin|new) (a )?(trip|drive|ride)|^start$/.test(s)) return { cmd: "start" };
  if (/(stop|end|finish|save) (the )?(trip|drive|ride)|^stop$/.test(s)) return { cmd: "stop" };
  if (/resume|continue/.test(s)) return { cmd: "resume" };
  if (/pause/.test(s)) return { cmd: "pause" };
  if (/kilomet|km|metric/.test(s)) return { cmd: "unit", value: "kmh" };
  if (/mile|mph|imperial/.test(s)) return { cmd: "unit", value: "mph" };
  if (/distance|how far/.test(s)) return { cmd: "distance" };
  if (/time|how long|duration/.test(s)) return { cmd: "time" };
  if (/top speed|max/.test(s)) return { cmd: "max" };
  if (/speed limit|the limit/.test(s)) return { cmd: "limit" };
  if (/(be quiet|mute|silence|stop talking)/.test(s)) return { cmd: "mute" };
  if (/(unmute|talk to me|voice on)/.test(s)) return { cmd: "unmute" };
  if (/speed|how fast|going/.test(s)) return { cmd: "speed" };
  return null;
}

export function useVoiceCommands(onCommand) {
  const [listening, setListening] = useState(false);
  const [heard, setHeard] = useState("");
  const rec = useRef(null);
  const want = useRef(false);
  const cb = useRef(onCommand); cb.current = onCommand;
  const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const stopL = () => { want.current = false; try { rec.current?.stop(); } catch {} setListening(false); };
  const startL = () => {
    if (!SR) return false;
    const r = new SR(); r.lang = navigator.language || "en-US"; r.continuous = true; r.interimResults = false;
    r.onresult = (e) => { const t = e.results[e.results.length - 1][0].transcript; setHeard(t); cb.current(t); };
    r.onend = () => { if (want.current) { try { r.start(); } catch {} } else setListening(false); };
    r.onerror = (e) => { if (e.error === "not-allowed") { want.current = false; setListening(false); } };
    rec.current = r; want.current = true;
    try { r.start(); setListening(true); } catch { return false; }
    return true;
  };
  useEffect(() => () => { want.current = false; try { rec.current?.stop(); } catch {} }, []);
  return { supported: !!SR, listening, heard, start: startL, stop: stopL };
}

export function useWakeLock(on) {
  useEffect(() => {
    if (!on || !("wakeLock" in navigator)) return;
    let lock;
    const req = () => navigator.wakeLock.request("screen").then((l) => { lock = l; }).catch(() => {});
    req();
    const vis = () => document.visibilityState === "visible" && req();
    document.addEventListener("visibilitychange", vis);
    return () => { document.removeEventListener("visibilitychange", vis); lock?.release().catch(() => {}); };
  }, [on]);
}

export function useSpeedLimit(fix, apiGet) {
  const [info, setInfo] = useState(null);
  const lastQ = useRef(null);
  useEffect(() => {
    if (!fix || fix.accuracy > 60) return;
    const here = [fix.lat, fix.lon], now = Date.now();
    const lq = lastQ.current;
    if (lq && haversine(lq.pt, here) < 60 && now - lq.t < 30000) return;
    lastQ.current = { pt: here, t: now };
    (async () => {
      try {
        setInfo((await apiGet(`/speedo/limit?lat=${fix.lat.toFixed(5)}&lon=${fix.lon.toFixed(5)}`)).data);
      } catch { setInfo((x) => x || { available: false }); }
    })();
  }, [fix, apiGet]);
  return info;
}
